import { useCallback } from 'react';

interface UseErrorHandlerOptions {
  onError?: (message: string) => void;
}

/**
 * Custom hook for consistent error handling across the application
 */
export const useErrorHandler = (options: UseErrorHandlerOptions = {}) => {
  const { onError } = options;

  const handleError = useCallback((error: unknown, defaultMessage: string = 'An error occurred') => {
    console.error(error);

    let errorMessage = defaultMessage;

    // Extract error message from different error formats
    const err = error as Record<string, unknown>;
    const response = err?.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;
    if (data?.detail && typeof data.detail === 'string') {
      errorMessage = data.detail;
    } else if (err?.message && typeof err.message === 'string') {
      errorMessage = err.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    if (onError) {
      onError(errorMessage);
    }

    return errorMessage;
  }, [onError]);

  const createErrorHandler = useCallback((defaultMessage: string) => {
    return (error: unknown) => handleError(error, defaultMessage);
  }, [handleError]);

  return {
    handleError,
    createErrorHandler
  };
}; 